import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Wallet, AlertTriangle, Plus, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { usePdpplPolicy } from "@/components/PrivacyConsentCheckbox";
import { PRODUCTS_BY_SLUG } from "@/lib/productCatalog";
import { useAuth } from "@/contexts/AuthContext";
import { fmt as fmtMoney } from "@/lib/currency";
import { WorkflowHero } from "@/components/workflow/WorkflowHero";
import { WorkflowSupportStrip } from "@/components/workflow/WorkflowSupportStrip";
import { WorkflowCreditUpsell } from "@/components/workflow/WorkflowCreditUpsell";
import { WorkflowSuccessPanel } from "@/components/workflow/WorkflowSuccessPanel";
import { SendCaseProvider } from "@/components/workflow/SendCaseContext";
import { SendCaseFormLeft } from "@/components/workflow/SendCaseFormLeft";
import { SendCaseFormRight } from "@/components/workflow/SendCaseFormRight";
import { WorkflowStepIndicator } from "@/components/workflow/WorkflowStepIndicator";
import { BatchQueuePanel } from "@/components/workflow/BatchQueuePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public send-a-case landing page. State + side-effects live here; the
// presentational chunks (Hero / SupportStrip / Upsell / SuccessPanel) and the
// form columns (SendCaseFormLeft / Right + WorkflowVerifyPanel) all live in
// `/components/workflow/`. Form state is shared via `SendCaseContext`.

export default function Workflow() {
    const { user } = useAuth();
    const formRef = useRef(null);
    const [creditAccount, setCreditAccount] = useState(null);
    const [form, setForm] = useState({
        clinic_name: user?.clinic_name || "",
        dentist_name: user?.dentist_name || user?.name || "",
        email: user?.email || "",
        phone: user?.phone || "",
        patient_id: "",
        patient_name: "",
        // Default order_date to today — the most common case. Backlog imports
        // can override via the date input.
        order_date: new Date().toISOString().slice(0, 10),
        expected_delivery: "",
        attached_materials: [],
        product: "permanent-crown",
        shade: "A2",
        arch: "upper",
        material: "",
        span_from: "",
        span_to: "",
        notes: "",
    });
    const [teeth, setTeeth] = useState([]);
    const [files, setFiles] = useState([]);
    const [drag, setDrag] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [consent, setConsent] = useState(false);
    // Backlog flag — only shown to lab staff (technician / lead_technician / employee / superadmin).
    // Catches the case where a tech is entering historic/scanned-offline work that's already
    // mid-flight, so it should skip SLA tracking + auto-balance.
    const [isBacklog, setIsBacklog] = useState(false);
    const [poNumber, setPoNumber] = useState("");
    const isLabStaff = ["technician", "lead_technician", "employee", "superadmin"].includes(user?.role || "");
    const pdpplPolicy = usePdpplPolicy();
    // Email-verification state machine. For anonymous (non-logged-in) submitters
    // we require a 6-digit code emailed to the case's contact address before we
    // create the case (anti-spam). Authenticated client users skip this.
    const [emailVerify, setEmailVerify] = useState(null);  // {verification_id, email, resend_cooldown_seconds}
    const [verifyCode, setVerifyCode] = useState("");
    const [verifyError, setVerifyError] = useState(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [pendingPayload, setPendingPayload] = useState(null);  // payload to submit after code is verified

    // Batch queue — when a doctor wants to submit multiple cases for the
    // same clinic in one go. Each entry holds the FULL backend payload plus
    // a small display summary (productLabel, toothCount, fileCount) for the
    // queue panel. Common fields (clinic / dentist / email / phone) come
    // from the active `form` at submit time, so the queue stays accurate
    // even if the doctor later corrects the clinic name.
    const [batchQueue, setBatchQueue] = useState([]);
    // Batch submission progress for the multi-case submit loop.
    // Shape: { current, total, completed: [{case_number, purchase_order_number}], failed: [{idx, error}] }
    const [batchProgress, setBatchProgress] = useState(null);
    // "Same as last queued patient" — when checked, pre-fills patient_id +
    // patient_name from the most-recent queued case after each "Add to batch".
    const [sameAsLast, setSameAsLast] = useState(false);

    // First-timer credit-pack upsell CTA — show only if not yet topped up.
    const [upsellDismissed, setUpsellDismissed] = useState(() => {
        try { return sessionStorage.getItem("tr_workflow_upsell_dismissed") === "1"; }
        catch { return false; }
    });
    const [upsellInView, setUpsellInView] = useState(false);
    const sectionRef = useRef(null);
    useEffect(() => {
        const el = sectionRef.current;
        if (!el || typeof IntersectionObserver === "undefined") return;
        const io = new IntersectionObserver(
            ([entry]) => setUpsellInView(entry.isIntersecting),
            { threshold: 0.05 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);
    const dismissUpsell = () => {
        setUpsellDismissed(true);
        try { sessionStorage.setItem("tr_workflow_upsell_dismissed", "1"); }
        catch (err) { console.warn("[workflow] upsell-dismiss persist failed:", err?.message); }
    };
    // Show the upsell when: section is in view, user hasn't dismissed it, AND
    // either anonymous OR (client with no lifetime top-ups yet — i.e. true first-timer).
    const showUpsell =
        !success &&
        upsellInView &&
        !upsellDismissed &&
        (!user || (user.role === "client" && (creditAccount?.lifetime_topup_qar ?? 0) === 0));

    // Load logged-in clinic's credit balance (anonymous public submissions skip this)
    useEffect(() => {
        if (!user || user.role !== "client") return;
        let alive = true;
        axios.get(`${API}/credits/account`).then((r) => { if (alive) setCreditAccount(r.data); }).catch(() => { /* noop — auth or network */ });
        return () => { alive = false; };
    }, [user]);

    useEffect(() => {
        if (window.location.hash === "#send-case") {
            setTimeout(() => {
                document.getElementById("send-case")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 60);
        }
    }, []);

    // Tick down the resend cooldown so the user has clear visual feedback.
    useEffect(() => {
        if (resendCooldown <= 0) return undefined;
        const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [resendCooldown]);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onFiles = async (incoming) => {
        const arr = Array.from(incoming || []);
        const valid = arr.filter((f) => /\.(stl|ply|obj|dcm|zip)$/i.test(f.name));
        const rejected = arr.length - valid.length;
        if (rejected > 0) toast.warning(`${rejected} file(s) skipped — only STL/PLY/OBJ/DCM/ZIP accepted`);
        if (valid.length === 0) return;
        // Upload each in parallel, store {id,name,size,type}
        const uploaded = [];
        for (const f of valid) {
            try {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await axios.post(`${API}/files/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(data);
            } catch (err) {
                toast.error(`Upload failed: ${f.name}`);
            }
        }
        if (uploaded.length > 0) toast.success(`${uploaded.length} file(s) uploaded`);
        setFiles((prev) => [...prev, ...uploaded].slice(0, 8));
    };

    const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

    // Build the case payload from form state. Pure — no side effects.
    const buildCasePayload = () => {
        const product = PRODUCTS_BY_SLUG[form.product];
        let toothNumbers = [];
        if (product.needs === "teeth") toothNumbers = teeth;
        else if (product.needs === "span") {
            const a = Number(form.span_from), b = Number(form.span_to);
            const lo = Math.min(a, b), hi = Math.max(a, b);
            toothNumbers = Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
        }
        const specifications = {};
        if (product.needs === "arch") specifications.arch = form.arch;
        if (product.needs === "span") {
            specifications.span_from = Number(form.span_from);
            specifications.span_to = Number(form.span_to);
        }
        if (form.material) specifications.material = form.material;
        return {
            clinic_name: form.clinic_name,
            dentist_name: form.dentist_name,
            email: form.email,
            phone: form.phone,
            patient_id: form.patient_id,
            patient_name: form.patient_name.trim() || null,
            order_date: form.order_date || null,
            expected_delivery: form.expected_delivery || null,
            attached_materials: form.attached_materials || [],
            product: form.product,
            shade: product.shade ? form.shade : "—",
            notes: form.notes,
            tooth_numbers: toothNumbers,
            specifications,
            files: files.map((f) => ({ id: f.id || null, name: f.name, size: f.size, type: f.type || null })),
            consent_pdppl: true,
            consent_pdppl_version: pdpplPolicy?.version || "1.0",
            // Lab-staff-only flag. Server ignores it for non-staff sessions.
            is_backlog: isLabStaff && isBacklog,
            // Optional clinic-side PO number for invoice routing.
            purchase_order_number: poNumber.trim() || null,
        };
    };

    const resetFormState = () => {
        setForm({
            clinic_name: "",
            dentist_name: "",
            email: "",
            phone: "",
            patient_id: "",
            patient_name: "",
            order_date: new Date().toISOString().slice(0, 10),
            expected_delivery: "",
            attached_materials: [],
            product: "permanent-crown",
            shade: "A2",
            arch: "upper",
            material: "",
            span_from: "",
            span_to: "",
            notes: "",
        });
        setTeeth([]);
        setFiles([]);
        setConsent(false);
        setIsBacklog(false);
        setPoNumber("");
        setEmailVerify(null);
        setVerifyCode("");
        setPendingPayload(null);
        setBatchQueue([]);
        setBatchProgress(null);
        setSameAsLast(false);
    };

    // Reset ONLY the per-case fields after a case is queued. Common fields
    // (clinic_name, dentist_name, email, phone, dates, attached_materials)
    // stay intact so the doctor doesn't re-type them for every case in the
    // batch. Patient identifiers are optionally pre-filled from the last
    // queued case via the "Same as last queued patient" checkbox.
    const resetPerCaseFields = (prefillFromLast) => {
        setForm((f) => ({
            ...f,
            patient_id: prefillFromLast?.patient_id || "",
            patient_name: prefillFromLast?.patient_name || "",
            product: "permanent-crown",
            shade: "A2",
            arch: "upper",
            material: "",
            span_from: "",
            span_to: "",
            notes: "",
        }));
        setTeeth([]);
        setFiles([]);
    };

    const validateActiveCase = () => {
        const product = PRODUCTS_BY_SLUG[form.product];
        if (!product) return "Pick a clinical product to continue";
        if (!form.clinic_name || !form.dentist_name || !form.email || !form.patient_id) {
            return "Please fill clinic, dentist, email and patient ID";
        }
        if (product.needs === "teeth" && teeth.length === 0) return "Select at least one tooth on the chart";
        if (product.needs === "span" && (!form.span_from || !form.span_to)) return "Set the span — first and last tooth";
        if (product.needs === "arch" && !form.arch) return "Choose the arch (upper / lower / both)";
        return null;
    };

    const addToBatch = () => {
        const err = validateActiveCase();
        if (err) { toast.error(err); return; }
        const payload = buildCasePayload();
        const product = PRODUCTS_BY_SLUG[form.product];
        const summary = {
            uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `q-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
            payload,
            patient_id: form.patient_id,
            patient_name: form.patient_name?.trim() || "",
            product: form.product,
            productLabel: product?.label || form.product,
            toothCount: payload.tooth_numbers?.length || 0,
            fileCount: payload.files?.length || 0,
        };
        const lastForCarry = sameAsLast ? { patient_id: form.patient_id, patient_name: form.patient_name } : null;
        setBatchQueue((q) => [...q, summary]);
        resetPerCaseFields(lastForCarry);
        toast.success(`Case ${batchQueue.length + 1} added to batch`);
    };

    const removeFromBatch = (idx) => {
        setBatchQueue((q) => q.filter((_, i) => i !== idx));
    };

    const handleSameAsLast = (checked, lastQueued) => {
        setSameAsLast(checked);
        if (checked && lastQueued) {
            setForm((f) => ({
                ...f,
                patient_id: lastQueued.patient_id || f.patient_id,
                patient_name: lastQueued.patient_name || f.patient_name,
            }));
        }
    };

    const submitCase = async (payload) => {
        // POST /cases — succeeds if either (a) the request is authenticated, OR
        // (b) the payload carries a valid email_verification_id + code.
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/cases`, payload);
            setSuccess(data);
            toast.success(`Case ${data.case_number} received`);
            resetFormState();
            setTimeout(() => {
                document.getElementById("send-case-success")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100);
        } catch (err) {
            console.error(err);
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail || "Submission failed. Please try again.";
            if (status === 402) {
                toast.error(typeof detail === "string" ? detail : "Insufficient credits", { duration: 14000, action: { label: "Buy credits", onClick: () => { window.location.href = "/portal#credits"; } } });
            } else if (status === 401 && payload.email_verification_code) {
                // Code was wrong — keep them on the verify step and surface the error inline.
                setVerifyError(typeof detail === "string" ? detail : "Wrong code.");
            } else {
                toast.error(typeof detail === "string" ? detail : "Submission failed");
            }
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Submit every case in the current batch (queue + the active form) sequentially.
     *
     * International best practice (e.g. Stripe's "Invoice items" endpoint
     * pattern): row-level retry over all-or-nothing. We continue past per-case
     * failures so the doctor doesn't lose 4 valid cases because 1 had bad data,
     * and we surface the failed cases on the success panel so they know what
     * to fix and resubmit. Each case gets its OWN backend POST → its OWN
     * case_number + purchase_order_number.
     */
    const submitBatch = async (verificationCtx) => {
        // Build the full list — queue first, then the current active case.
        const activeErr = validateActiveCase();
        if (activeErr) { toast.error(activeErr); return; }
        const activePayload = buildCasePayload();
        const allCases = [
            ...batchQueue.map((q) => ({ ...q.payload })),
            activePayload,
        ];
        // Anonymous submissions: the verification code unlocks the ENTIRE
        // batch (single 6-digit code per email). Attach to every payload.
        if (verificationCtx) {
            for (const p of allCases) {
                p.email_verification_id = verificationCtx.verification_id;
                p.email_verification_code = verificationCtx.code;
            }
        }
        setSubmitting(true);
        setBatchProgress({ current: 0, total: allCases.length, completed: [], failed: [] });
        const completed = [];
        const failed = [];
        for (let i = 0; i < allCases.length; i++) {
            setBatchProgress({ current: i + 1, total: allCases.length, completed: [...completed], failed: [...failed] });
            try {
                const { data } = await axios.post(`${API}/cases`, allCases[i]);
                completed.push({
                    case_number: data.case_number,
                    purchase_order_number: data.purchase_order_number,
                    patient_id: allCases[i].patient_id,
                    product: allCases[i].product,
                });
            } catch (err) {
                const status = err?.response?.status;
                const detail = err?.response?.data?.detail || "Submission failed";
                // If the verification code itself failed on the very first
                // case, stop the loop — every subsequent case would fail too.
                if (status === 401 && allCases[i].email_verification_code && i === 0) {
                    setVerifyError(typeof detail === "string" ? detail : "Wrong code.");
                    setBatchProgress(null);
                    setSubmitting(false);
                    return;
                }
                failed.push({
                    idx: i,
                    patient_id: allCases[i].patient_id,
                    product: allCases[i].product,
                    error: typeof detail === "string" ? detail : "Submission failed",
                });
            }
        }
        setBatchProgress({ current: allCases.length, total: allCases.length, completed, failed });
        setSubmitting(false);
        if (completed.length === 0) {
            toast.error("No cases were accepted — please review the errors and try again");
            return;
        }
        if (failed.length === 0) {
            toast.success(`${completed.length} case${completed.length !== 1 ? "s" : ""} submitted successfully`);
        } else {
            toast.warning(`${completed.length} of ${allCases.length} cases submitted · ${failed.length} failed — see details below`);
        }
        // Set the success-panel state to the LAST successful case so the
        // existing WorkflowSuccessPanel surfaces correctly; the full batch
        // results are passed through `batchProgress` for the multi-row view.
        setSuccess({
            ...completed[completed.length - 1],
            _batch: { completed, failed, total: allCases.length },
        });
        setBatchQueue([]);
        setSameAsLast(false);
        setTimeout(() => {
            document.getElementById("send-case-success")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    const submit = async (e) => {
        e.preventDefault();
        const hasBatch = batchQueue.length > 0;
        const activeErr = validateActiveCase();
        if (activeErr) { toast.error(activeErr); return; }
        if (!consent) { toast.error("Please accept the Qatar PDPPL data-privacy terms before submitting."); return; }

        // Authenticated client portal users skip the email-verification gate.
        if (user && user.role === "client") {
            if (hasBatch) await submitBatch(null);
            else await submitCase(buildCasePayload());
            return;
        }
        // Anonymous public submitters: request a 6-digit code (ONE code per
        // email — unlocks the whole batch). Same flow whether 1 case or 10.
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/cases/start-email-verification`, {
                email: form.email,
                clinic_name: form.clinic_name,
                dentist_name: form.dentist_name,
            });
            setEmailVerify(data);
            setResendCooldown(data.resend_cooldown_seconds || 30);
            // Stash the active payload alongside batch state so submitWithCode
            // can decide single vs batch when the code comes back.
            setPendingPayload(hasBatch ? { _isBatch: true } : buildCasePayload());
            setVerifyCode("");
            setVerifyError(null);
            toast.success(`Verification code sent to ${data.email}${hasBatch ? ` — unlocks all ${batchQueue.length + 1} cases` : ""}`);
        } catch (err) {
            console.error(err);
            const detail = err?.response?.data?.detail || "Could not send verification code.";
            toast.error(typeof detail === "string" ? detail : "Could not send verification code.");
        } finally {
            setSubmitting(false);
        }
    };

    const submitWithCode = async (e) => {
        e?.preventDefault?.();
        setVerifyError(null);
        if (!/^\d{6}$/.test(verifyCode)) { setVerifyError("Enter the 6-digit code from your email."); return; }
        if (!pendingPayload || !emailVerify) return;
        // Batch flow — submitBatch attaches the code to every case.
        if (pendingPayload._isBatch) {
            await submitBatch({ verification_id: emailVerify.verification_id, code: verifyCode });
            return;
        }
        // Single-case flow — unchanged from pre-batch behaviour.
        await submitCase({
            ...pendingPayload,
            email_verification_id: emailVerify.verification_id,
            email_verification_code: verifyCode,
        });
    };

    const resendVerifyCode = async () => {
        if (resendCooldown > 0 || !emailVerify) return;
        try {
            const { data } = await axios.post(`${API}/cases/resend-email-verification`, { verification_id: emailVerify.verification_id });
            setResendCooldown(data.resend_cooldown_seconds || 30);
            setVerifyCode("");
            setVerifyError(null);
            toast.success("New code sent. Check your email.");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Could not resend code.";
            toast.error(typeof detail === "string" ? detail : "Could not resend code.");
        }
    };

    const activeProduct = PRODUCTS_BY_SLUG[form.product];

    // The step-indicator + verify flow only apply to anonymous submitters —
    // logged-in clients post straight to /cases without an email gate.
    const isAnonymous = !user || user.role !== "client";

    return (
        <div data-testid="workflow-page" className="bg-white">
            <WorkflowHero />

            {/* SEND A CASE PORTAL */}
            <section ref={sectionRef} id="send-case" className="bg-[#0B1120] tr-grain text-white relative overflow-hidden" data-testid="send-case-portal">
                <div className="absolute -top-32 right-1/3 w-[500px] h-[500px] tr-glow bg-cyan-500/25" />
                <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] tr-glow bg-teal-500/20" />
                <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 py-24 lg:py-28">
                    <div className="grid lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-7">
                            <div className="tr-overline text-cyan-400 mb-4">· Send a case · secure portal</div>
                            <h2 className="font-display font-light text-5xl lg:text-6xl tracking-tight leading-[0.98]">
                                One form.<br /><span className="italic text-cyan-300/90 font-extralight">Your case is in print.</span>
                            </h2>
                            {/* Anonymous submitters get a 3-step breadcrumb so they know what
                                happens after "submit" (i.e. an email-code step, not instant). */}
                            {isAnonymous && (
                                <WorkflowStepIndicator step={success ? 3 : emailVerify ? 2 : 1} />
                            )}
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9 self-end">
                            <p className="text-white/65 text-[15px] leading-[1.75]">
                                We treat patient identifiers as PHI. Files are TLS-encrypted in transit and access-logged.
                            </p>
                            {creditAccount && (
                                <div data-testid="workflow-credit-pill" className={`mt-4 rounded-xl border ${creditAccount.balance_qar < 1000 ? "border-rose-400/50 bg-rose-950/40" : "border-cyan-400/30 bg-cyan-950/30"} px-4 py-3 flex items-center gap-3`}>
                                    {creditAccount.balance_qar < 1000 ? <AlertTriangle size={16} className="text-rose-300 shrink-0" /> : <Wallet size={16} className="text-cyan-300 shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-mono-tr text-[10px] tracking-[0.22em] uppercase text-white/60">Your credit balance</div>
                                        <div className="font-display text-xl tracking-tight text-white">{fmtMoney(creditAccount.balance_qar || 0, "QAR")}</div>
                                    </div>
                                    <Link to="/portal#credits" className="text-[12px] font-medium text-cyan-300 hover:text-cyan-200 whitespace-nowrap">Top up →</Link>
                                </div>
                            )}
                        </div>
                    </div>

                    {success ? (
                        <WorkflowSuccessPanel success={success} onSubmitAnother={() => { setSuccess(null); setBatchProgress(null); }} />
                    ) : (
                        <SendCaseProvider value={{
                            form, setForm, update,
                            teeth, setTeeth,
                            files, setFiles, drag, setDrag, onFiles, removeFile,
                            consent, setConsent,
                            isBacklog, setIsBacklog, isLabStaff,
                            poNumber, setPoNumber,
                            emailVerify, setEmailVerify, setPendingPayload,
                            verifyCode, setVerifyCode,
                            verifyError, setVerifyError,
                            resendCooldown, submitting,
                            submitWithCode, resendVerifyCode,
                            activeProduct,
                            // Batch-mode extras (iter 56) — the right-column
                            // submit button uses these to swap label between
                            // "Submit case" and "Submit N cases".
                            batchSize: batchQueue.length,
                            batchProgress,
                        }}>
                            <form
                                ref={formRef}
                                onSubmit={submit}
                                data-testid="send-case-form"
                                className="rounded-3xl bg-white text-slate-900 p-7 lg:p-10 space-y-6"
                            >
                                {/* Queue panel surfaces above the form whenever
                                    there's at least one queued case. */}
                                <BatchQueuePanel
                                    queue={batchQueue}
                                    onRemove={removeFromBatch}
                                    onSameAsLast={handleSameAsLast}
                                    sameAsLastActive={sameAsLast}
                                />
                                <div className="grid lg:grid-cols-12 gap-7">
                                    <SendCaseFormLeft />
                                    <SendCaseFormRight />
                                </div>
                                {/* "Add another case to this batch" — only renders when
                                    the doctor is NOT in the verification step (the verify
                                    panel takes over the right column at that point and a
                                    new "Add" would be confusing). */}
                                {!emailVerify && (
                                    <div
                                        data-testid="add-to-batch-row"
                                        className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100"
                                    >
                                        <div className="text-[12.5px] text-slate-500 max-w-md leading-relaxed">
                                            Got more than one case to send? Add this one to the batch and start another — patient details and product can differ per case, contact info stays the same.
                                        </div>
                                        <button
                                            type="button"
                                            onClick={addToBatch}
                                            disabled={submitting}
                                            data-testid="add-to-batch-btn"
                                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-300 bg-cyan-50 hover:bg-cyan-100 text-cyan-800 font-medium text-[13px] transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                            Add another case to batch
                                        </button>
                                    </div>
                                )}
                            </form>
                        </SendCaseProvider>
                    )}
                </div>
            </section>

            <WorkflowSupportStrip />

            <style>{`
                .tr-input {
                    width: 100%;
                    background: #fff;
                    border: 1px solid var(--tr-line);
                    border-radius: 10px;
                    padding: 0.7rem 0.85rem;
                    font-size: 14px;
                    color: var(--tr-ink);
                    transition: border-color 0.15s ease, box-shadow 0.15s ease;
                    font-family: inherit;
                }
                .tr-input:focus {
                    border-color: var(--tr-cyan);
                    box-shadow: 0 0 0 3px rgba(6,182,212,0.15);
                    outline: none;
                }
                @keyframes tr-upsell-in {
                    from { opacity: 0; transform: translateY(14px) scale(0.96); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .tr-upsell-card {
                    animation: tr-upsell-in 320ms cubic-bezier(0.22, 1, 0.36, 1) both;
                }
            `}</style>

            <WorkflowCreditUpsell show={showUpsell} onDismiss={dismissUpsell} hasUser={!!user} />
        </div>
    );
}
